/**
 * Controls Bot interaction with Chat Widget.
 */
import {
    flowActionTypes,
    flowActionIds,
    faqTypes,
    lgTypes,
    lcTypes,
    ticketStages,
    requestStages,
    searchStages,
    linkResponseTypes,
    searchResponseTypes,
    actionResponseTypes,
    paramTypes,
    paramResponseTypes,
    bookingStages,
    patronRecordStages,
    patronRecordResponses,
} from './flowConstants.js';
import BotMessage from './BotMessage.js';
import { ChatAnswer } from '../libchat/models/ChatQuestion.js';
import { botErrorCodes } from './BotError.js';
import { BotFlow, ResponseResult } from './botFlow.js';

class BotController {
    #isChatOnline = false;

    constructor(
        botService,
        templateEng,
        flow,
        emitter,
        patronSession,
        widget,
    ) {
        /** @prop {BotService} */
        this.botService = botService;
        /** @prop  */
        this.templateEng = templateEng;
        /** @prop {BotFlow} */
        this.flow = flow;
        /** @prop {EventEmitter} */
        this.emitter = emitter;
        /** @prop {patronSession} */
        this.patronSession = patronSession;
        /** @prop {ChatWidget} */
        this.widget = widget;

        this.isStarted = false;
        this.isSkipped = false;

        /** @prop {Number} milliseconds to pause between subsequent messages. */
        this.messagePause = this.flow.message_delay * 1000;

        /** @prop {Number|null} botTimeoutId Set Timeout function id */
        this.botTimeoutId = null;
    }

    /**
     * Is bot setup at all and/or has it been skipped.
     * @returns {Boolean}
     */
    isBotEnabled() {
        return !this.isSkipped && this.flow.uuid !== '';
    }

    /**
     * Is the bot enabled and set to work offline.
     * @returns {Boolean}
     */
    isBotActiveOffline() {
        return this.isBotEnabled() && this.flow.isAvailableOffline();
    }

    /**
     * Is the bot enabled and set to work online.
     * @returns {Boolean}
     */
    isBotActiveOnline() {
        if (!this.isBotEnabled()) {
            return false;
        }
        return this.flow.isAvailableOnline(this.widget.get('online_rule'));
    }

    /**
     * When the timeout is reached we reset the bot.
     */
    _timeoutBot() {
        this.emitter.trigger('bot:reset');
    }

    /**
     * Clear the bot timeout.
     */
    _clearBotTimeout() {
        if (this.botTimeoutId !== null) {
            clearTimeout(this.botTimeoutId);
            this.botTimeoutId = null;
        }
    }

    /**
     * Clear then reset the bot timeout
     * After any patron actions.
     */
    _resetBotTimeout() {
        this._clearBotTimeout();

        this.botTimeoutId = setTimeout(
            this._timeoutBot.bind(this),
            this.flow.timeout * 60 * 1000, // convert minutes to milliseconds
        );
    }

    /**
     * A message object to sent to chat view.
     * @param {FlowAction} action Action to trigger.
     * @param {String} text Last response text (if any)
     * @param {Number} id Last response id
     * @returns {Array<BotMessage>}
     */
    async _generateMessagesFromAction(action, { text = '', id = 0 }) {
        if (!action) {
            return [];
        }
        const messages = [];
        // store text retrieved via ajax that will go into the action messages
        let resultText = '';
        if (action.isSearchAction() && action.stage !== searchStages.PROMPT) {
            try {
                action.stage = searchStages.RESULT;
                resultText = await this.botService.searchAction(action, this.patronSession.getChannel(), text);
            } catch (error) {
                action.stage = searchStages.FAIL;
                if (error.code !== botErrorCodes.noResults) {
                    resultText = error.message;
                }
            }

            const parentAction = Number(action?.parentSearchActionId || 0);
            if (parentAction > 0) {
                const textToSave = `${text} [Chained Search]`;
                this.botService.saveSessionAction(
                    this.patronSession.getChannel(),
                    action,
                    {
                        response: textToSave,
                    },
                );
            }
        }
        if (action.type === flowActionTypes.ACTION_FAQ
            && action.subType === faqTypes.FAQ_SINGLE) {
            resultText = await this.botService.getFAQ(action.subTypeId, action.faqLinkText);
        }
        if (action.type === flowActionTypes.ACTION_FAQ
            && action.subType === faqTypes.FAQ_RESULTS) {
            try {
                resultText = await this.botService.searchAction(action, this.patronSession.getChannel(), action.prompt);
            } catch (error) {
                resultText = error.code === botErrorCodes.noResults
                    ? action.fail_message
                    : error.message;
            }
        }

        if (action.type === flowActionTypes.ACTION_LIBGUIDES) {
            if (action.subType === lgTypes.GUIDE_SINGLE) {
                resultText = await this.botService.getGuide(
                    action.subTypeId,
                    action.siteId,
                    this.patronSession.getChannel(),
                );
            } else if (action.subType === lgTypes.AZ_SINGLE) {
                resultText = await this.botService.getDatabase(
                    action.subTypeId,
                    action.siteId,
                    this.patronSession.getChannel(),
                );
            }
        }

        if (action.type === flowActionTypes.ACTION_LIBCAL
            && action.subType === lcTypes.BOOKING_APPT
            && action.botAppt) {
            // put the later stages first so they can fallback to earlier ones...
            if (action.stage === bookingStages.DATE_PROMPT || action.stage === bookingStages.TIME_PROMPT) {
                try {
                    // Returns a message in case we need to say "sorry no dates/times found please try again."
                    // in which case the service changed the stage
                    resultText = await this.botService.getBookingAvailability(
                        action,
                        this.patronSession.getChannel(),
                        this.widget.get('language'),
                    );
                } catch (error) {
                    action.stage = bookingStages.FAIL;
                    resultText = error.message || this.flow.error_generic;
                }
            }
            if (action.stage === bookingStages.GROUP_PROMPT && !action.isLocationInfoSet()) {
                try {
                    await this.botService.getBookingLocationInfo(
                        action,
                        this.patronSession.getChannel(),
                    );
                } catch (error) {
                    action.stage = bookingStages.FAIL;
                    resultText = error.message || this.flow.error_generic;
                }
            }
        }

        if (action.type === flowActionTypes.ACTION_LIBCAL
            && action.subType !== lcTypes.EVENT_SEARCH
            && !action.botAppt) {
            if (action.isHours()) {
                resultText = await this.botService.getLCHours(
                    action,
                    this.patronSession.getChannel(),
                );
            } else {
                resultText = await this.botService.getLocation(
                    action,
                    this.patronSession.getChannel(),
                );
            }
        }

        if (action.type === flowActionTypes.ACTION_LIBWIZARD) {
            resultText = await this.botService.getLibWizardItem(
                action,
                this.patronSession.getChannel(),
            );
        }

        if (action.type === flowActionTypes.ACTION_PATRON_RECORDS
            && action.stage === patronRecordStages.RESULTS
            && id !== patronRecordResponses.END
        ) {
            resultText = await this.botService.getPatronRecordData(
                action,
                id,
                this.patronSession.libraryCard,
                this.patronSession.pin,
                this.patronSession.getChannel(),
            );
        }

        const messageText = action.getMessages(resultText);
        const nextAction = this.flow.getAction(action.defaultNextAction);

        messageText.forEach((msg, i) => {
            const msgId = i + 1;
            const message = new BotMessage({
                actionId: action.id,
                actionType: action.type,
                msg,
                responses: messageText.length === msgId ? action.getResponses({}) : [],
            });

            if (
                [searchStages.RESULT, searchStages.FAIL].includes(action.stage)
                && action.isNextSearchChained()
                && nextAction?.isSearchAction()
                && messageText.length === msgId
            ) {
                // skip the prompt message
            } else {
                messages.push(message);
            }
        });

        return messages;
    }

    /**
     * Send end message and trigger end event.
     */
    _handleEndAction() {
        const message = new BotMessage({
            msg: this.flow.message_end,
        });
        this._sendMessagesToWidget([message]);

        // Tell server the chat is ended
        // if a ticket wasn't created.
        if (this.patronSession.ticketId === 0 && this.patronSession.getChannel() !== '') {
            this.botService.saveEndSession(this.patronSession.getChannel())
                .catch(() => {
                    // No need to display this in UI
                });
        }

        this._clearBotTimeout();

        this.emitter.trigger('bot:end', {});
    }

    /**
     *
     * @param {TicketAction} action Ticket action set to use form
     */
    _handleTicketForm(action) {
        this.botService.getQuestionForm(this.patronSession.getChannel(), action)
            .then(({ html, errormsg, translations, isAuthed, autoReplySettings, url, rules }) => {
                // isAuthed and bot was not.
                if (isAuthed && this.widget.get('auth_id') === 0) {
                    // show url instead.
                    const message = new BotMessage({
                        actionId: action.id,
                        actionType: action.type,
                        msg: `${this.widget.get('translation').ch_botticket_form}<br/><a href="${url}">${url}</a>`,
                    });
                    this._sendMessagesToWidget([message]);
                    // send > 0 to end chat.
                    setTimeout(() => {
                        this._handleNextAction(action, { id: 1 });
                    }, this.messagePause);
                    return;
                }
                this.emitter.trigger(
                    'bot:ticket:form',
                    { html, errormsg, translations, autoReplySettings, action, rules },
                );
            })
            .catch((error) => {
                this._sendErrorToWidget({ actionId: action.id, message: error.message });
                // sent to next action if there is one
                this._handleNextAction(action, {});
            });
    }

    /**
     * Extra fields for rating if using nps
     * @param {RatingAction} action Rating action
     */
    #handleRatingForm(action) {
        const message = new BotMessage({
            actionId: action.id,
            actionType: action.type,
            msg: action.getMessages().find(Boolean) || '', // first message
            responses: action.getResponses(),
        });
        message.survey_uuid = action.subTypeId;
        message.rate_1 = action.prompt;
        message.rate_4 = action.prompt2;

        this._sendMessagesToWidget([message]);
    }

    /**
     * @param {LibCalAction} action LC action set to book appts.
     */
    #handleBookingForm(action) {
        // get form and then show it in widget...
        this.botService.getBookingForm(
            action,
            this.patronSession.getChannel(),
            this.widget.get('language'),
        )
            .then(({ html = '' }) => {
                this.emitter.trigger(
                    'bot:booking:form',
                    { html, action },
                );
            })
            .catch((error) => {
                this._sendErrorToWidget({ actionId: action.id, message: error.message });
                // sent to next action if there is one
                this._handleNextAction(action, {});
            });
    }

    /**
     * Trigger widget events to show messages.
     * @param {Array<BotMessage>} messages Messages from action
     * @param {Boolean} endChat End chat after sending message.
     */
    _sendMessagesToWidget(messages, endChat = false) {
        messages.forEach((botMessage, i) => {
            // Add profile info to all.
            botMessage.name = this.flow.getName();
            botMessage.profile_url = this.flow.profile_url;
            botMessage.profile_color = this.flow.profile_color;
            botMessage.msgId = i + 1;
            if (i === 0) {
                this.emitter.trigger(
                    'bot:action',
                    {
                        botMessage,
                        endChat,
                    });
                return;
            }
            setTimeout(() => {
                this.emitter.trigger(
                    'bot:action',
                    {
                        botMessage,
                        endChat,
                    });
            }, this.messagePause * i); // increase this to account for more than 2 messages.
        });
    }

    /**
     * Send an error to the widget ui.
     * @param {Number} actionId Action where error was triggered
     * @param {String} message Error message
     * @param {Boolean} endChat Should chat end after this error
     */
    _sendErrorToWidget({ actionId = 0, message = '', endChat = false }) {
        if (!message) {
            message = this.flow.error_generic;
        }
        const botMessage = new BotMessage({
            actionId,
            msg: message,
            isError: true,
        });
        this._sendMessagesToWidget([botMessage], endChat);
    }

    /**
     * Handle actions that ask for no response.
     * They perform an action or trigger a next response.
     * @param {FlowAction} action Current action
     * @param {Number} prevMsgCount Number of messages previous action sends.
     * @param {String} text Search prompt to be chained to next action
     */
    _handleNoResponseActions(action, prevMsgCount = 1, text = '') {
        // chat... sends to chat
        if (action.type === flowActionTypes.ACTION_CHAT) {
            // send event to chatWidgetControl to attempt a chat.
            // but wait until the messages are shown.
            setTimeout(() => {
                const rule = action.getStatusRule();
                this.emitter.trigger('bot:sendtochat', { rule, actionId: action.id });
            }, (this.messagePause * prevMsgCount));
            return;
        }

        // Default just go to the next action...
        // a timeout on this so it doesn't show up RIGHT AWAY
        // timeout is extended if previous action is sending multiple messages
        // else it triggers too soon.
        setTimeout(() => {
            if (!action.isNextSearchChained()) {
                text = '';
            } else {
                action.stage = searchStages.CHAINED;
            }
            this._handleNextAction(action, { id: 0, text });
        }, (this.messagePause * prevMsgCount));
    }

    /**
     * Check if an action covers already saved data.
     * @param {FlowAction} action a
     * @returns {Object|null} Send back an reponse object else null
     */
    _checkForExistingResponse(action) {
        if ([
            flowActionTypes.ACTION_REQUEST_INFO,
            flowActionTypes.ACTION_QUESTION,
            flowActionTypes.ACTION_TICKET,
            flowActionTypes.ACTION_PATRON_RECORDS,
        ].indexOf(action.type) === -1) {
            return null;
        }

        if (action.type === flowActionTypes.ACTION_REQUEST_INFO) {
            let existing = '';
            switch (action.stage) {
                case requestStages.NAME:
                    existing = this.patronSession.get('name');
                    break;
                case requestStages.EMAIL:
                    existing = this.patronSession.get('client_contact');
                    break;
                case requestStages.ID:
                    existing = this.patronSession.get('local_id');
                    break;
            }
            if (!existing) {
                return null;
            }
            return {
                text: existing,
            };
        }

        if (action.type === flowActionTypes.ACTION_QUESTION) {
            // only saved answers.
            if (!action.saveAnswer) {
                return null;
            }
            const answer = this.patronSession.getAnswerByQuestionId(action.subType);
            if (!answer) {
                return null;
            }
            return {
                id: answer.responseId,
                text: answer.text,
            };
        }

        // ACTION_TICKET check if ticket already generated
        if (action.type === flowActionTypes.ACTION_TICKET) {
            // Has ticket already been created somehow.
            if (this.patronSession.ticketId > 0) {
                return {};
            }
            // Are we at the contact request and do we already have that info.
            if (!action.useForm
                && action.stage === ticketStages.CONTACT && this.patronSession.get('client_contact') !== '') {
                return {
                    text: this.patronSession.get('client_contact'),
                };
            }
            return null;
        }

        // check for existing pin/library card
        if (action.type === flowActionTypes.ACTION_PATRON_RECORDS) {
            let existing = '';
            switch (action.stage) {
                case patronRecordStages.CARD_PROMPT:
                    existing = this.patronSession.libraryCard;
                    break;
                case patronRecordStages.PIN_PROMPT:
                    existing = this.patronSession.pin;
                    break;
            }
            if (!existing) {
                return null;
            }
            return {
                text: existing,
            };
        }

        return null;
    }

    /**
     * Trigger a transfer to another flow.
     * @param {FlowAction} action Action
     * @param {String} flow_uuid Flow uuid to start up
     */
    _handleFlowTransfer(action, flow_uuid) {
        // Load new flow
        this.botService.getFlowTransfer(this.patronSession.getChannel(), flow_uuid)
            .then((flow) => {
                // set flow on controller and service...
                const flowClass = new BotFlow(flow);
                // set some props from old flow on new one
                flowClass.transferFlow(this.flow);
                this.flow = flowClass;
                this.botService.flow = this.flow;
                // Load the welcome action
                const welcome = this.flow.getWelcomeAction();
                // Trigger it's "start" response
                this._handleNextAction(welcome, { id: 1 });
            })
            .catch((error) => {
                this._sendErrorToWidget({ actionId: action.id, message: error.message });
                // Move on to default next action.
                this._handleNextAction(action, {});
            });
    }

    /**
     * See what next action will be based on params
     * @param {ParamAction} action Param check action
     */
    _handleParamsCheck(action) {
        // By default just goes to the default next action.
        const resp = {
            id: 0,
            text: '',
        };
        if (action.subType === paramTypes.PARAM_CHAT_STATUS) {
            // check chat status, apply rule.
            resp.id = this.#isChatOnline
                ? paramResponseTypes.PARAM_CHAT_STATUS_ONLINE
                : paramResponseTypes.PARAM_CHAT_STATUS_OFFLINE;
        }
        if (action.subType === paramTypes.PARAM_OPEN_HOURS) {
            // check hours.
            this.botService.checkOpenHours(action)
                .then((result = paramResponseTypes.PARAM_HOURS_CLOSED) => {
                    resp.id = result;
                    this._handleNextAction(action, resp);
                })
                .catch(() => {
                    // Move on to default next action.
                    this._handleNextAction(action, {});
                });
            return;
        }
        if (action.subType === paramTypes.PARAM_LC_HOURS) {
            // check hours.
            this.botService.checkLCHours(action, this.patronSession.getChannel())
                .then((result = paramResponseTypes.PARAM_HOURS_CLOSED) => {
                    resp.id = result;
                    this._handleNextAction(action, resp);
                })
                .catch(() => {
                    // Move on to default next action.
                    this._handleNextAction(action, {});
                });
            return;
        }

        this._handleNextAction(action, resp);
    }

    /**
     * Deal with the next action (some of which may rely on previous response)
     * @param {FlowAction} nextAction Action to process
     * @param {Object} lastResponse Last response
     * @param {String} lastResponse.text Free text reponse to prev action
     * @param {ResponseResult} respResult Result from prev action response (if applicable)
     */
    _processNextAction(nextAction, { text = '', id = 0 }, respResult = new ResponseResult({})) {
        // Pass along the response to the next action
        if (respResult.passAlong) {
            this._handleFreeText({
                actionId: respResult.nextActionId,
                text: respResult.text,
            });
            return;
        }
        // Params action check the param(s) and then will trigger another action
        if (nextAction.type === flowActionTypes.ACTION_PARAMS) {
            this._handleParamsCheck(nextAction);
            return;
        }

        const existingResponse = this._checkForExistingResponse(nextAction);
        if (existingResponse) {
            this._handleNextAction(nextAction, existingResponse);
            return;
        }

        if (nextAction.type === flowActionTypes.ACTION_RATING) {
            // if rating form already showed, we dont want to show it again
            // so lets skip to next action.
            if (document.getElementById('s-lch-rate-form')) {
                this._handleNextAction(nextAction, {});
                return;
            }
            this.#handleRatingForm(nextAction);
            return;
        }

        if (nextAction.type === flowActionTypes.ACTION_TICKET
            && nextAction.useForm && nextAction.stage === ticketStages.FORM) {
            this._handleTicketForm(nextAction);
            return;
        }

        if (nextAction.type === flowActionTypes.ACTION_LIBCAL
            && nextAction.botAppt && nextAction.stage === bookingStages.FORM) {
            this.#handleBookingForm(nextAction);
            return;
        }

        if (nextAction.isSearchAction() && respResult.chainNextSearch) {
            text = respResult.text;
            nextAction.stage = searchStages.RESULT;
        }

        if (nextAction.type === flowActionTypes.ACTION_TICKET) {
            nextAction.prompt2 = this.flow.provide_email;
        }

        this._generateMessagesFromAction(nextAction, { text, id })
            .then((messages) => {
                const mesagesWithResponses = messages.filter((el) => {
                    return el.responses?.length > 0;
                });
                this._sendMessagesToWidget(messages);
                if (mesagesWithResponses.length === 0) {
                    this._handleNoResponseActions(nextAction, messages.length, text);
                }
            })
            .catch(() => {
                this._sendErrorToWidget({
                    actionId: nextAction.id,
                });
                // Move on to default next action.
                this._handleNextAction(nextAction, {});
            });
    }

    /**
     * Find next action for response then process it
     * @param {FlowAction} action Current action
     * @param {Number} [id] Response id
     * @param {String} [text] Response text
     */
    _handleNextAction(action, { id = 0, text = '' }) {
        const respResult = action.getResultForResponse({ id, text });
        if (respResult.nextActionId === flowActionIds.ACTION_SKIP) {
            // skip to normal chat.
            this.isSkipped = true;
            this.emitter.trigger('bot:skipbot');
            return;
        }

        // send message if rule/response had it.
        if (respResult.message !== '') {
            const message = new BotMessage({
                actionId: action.id,
                actionType: action.type,
                msg: respResult.message,
            });
            this._sendMessagesToWidget([message]);
        }

        if (respResult.nextActionId === flowActionIds.ACTION_END) {
            this._handleEndAction();
            return;
        }
        if (respResult.nextActionId === flowActionIds.ACTION_TRANSFER) {
            this._handleFlowTransfer(action, respResult.flow_uuid);
            return;
        }

        const nextAction = this.flow.getAction(respResult.nextActionId);
        if (!nextAction) {
            this._sendErrorToWidget({
                actionId: respResult.nextActionId,
                endChat: true,
            });
            return;
        }

        if (
            respResult.chainNextSearch
            && nextAction.isSearchAction()
            && nextAction.id !== action.id
            && action?.parentSearchActionId !== nextAction.id
        ) {
            nextAction.parentSearchActionId = action.parentSearchActionId > 0 ? action.parentSearchActionId : action.id;
        }

        this._processNextAction(nextAction, { text, id }, respResult);
    }

    /**
     * Trigger a specific action from outside the normal flow.
     * @param {Number} actionId Action to trigger
     */
    _handleActionTrigger({ actionId = 0 }) {
        const action = this.flow.getAction(actionId);
        if (!action) {
            this._sendErrorToWidget({
                actionId,
                endChat: true,
                message: this.flow.error_generic,
            });
            return;
        }
        this._processNextAction(action, {});
    }

    /**
     * Store requestion info response.
     * @param {FlowAction} action Request info action
     * @param {String} text Patron response
     */
    async _storeRequestResponse(action, text) {
        switch (action.stage) {
            case requestStages.NAME:
                this.patronSession.set('name', text);
                break;
            case requestStages.EMAIL:
                this.patronSession.set('client_contact', text);
                break;
            case requestStages.ID:
                this.patronSession.set('local_id', text);
                break;
        }

        await this.botService.saveContactInfo(action, this.patronSession.getChannel(), text);
    }

    /**
     * If its the right stage, save ticket contact request.
     * @param {FlowAction} action Ticket action
     * @param {String} text patron response
     */
    async _saveTicketContactInfo(action, text) {
        if (action.stage !== ticketStages.CONTACT) {
            return;
        }
        this.patronSession.set('client_contact', text);
        await this.botService.saveContactInfo(action, this.patronSession.getChannel(), text);
    }

    /**
     * If set to, save answer to questions.
     * @param {FlowAction} action a
     * @param {String} text Text answer
     * @param {Number} id Reponse id
     */
    async _storeQuestionAnswer(action, { text = '', id = 0 }) {
        if (!action.saveAnswer) {
            // save as action instead
            this.botService.saveSessionAction(
                this.patronSession.getChannel(),
                action,
                {
                    response: text,
                    response_id: id,
                },
            );
            return;
        }
        const customQuestion = action.getQuestion();
        if (!customQuestion) {
            return;
        }
        const answer = new ChatAnswer({
            questionId: action.subType,
            question: customQuestion.question,
            text: text === '' ? customQuestion.getLabelByResponseId(id) : text,
            responseId: id,
        });
        this.patronSession.addAnswer(answer);

        await this.botService.saveQuestionAnswer(action, this.patronSession.getChannel(), { id, text });
    }

    /**
     * Save library card or pin to patron sessions
     * @param {FlowAction} action
     * @param {String} text Text answer
     */
    async #storePatronRecordResponse(action, text) {
        if (action.stage === patronRecordStages.CARD_PROMPT) {
            this.patronSession.libraryCard = text;
        }
        if (action.stage === patronRecordStages.PIN_PROMPT) {
            this.patronSession.pin = text;
        }
    }

    /**
     * Store response data (if necessary).
     * @param {FlowAction} action Current action
     * @param {String} text Freetext response
     * @param {Number} id Preset response id
     */
    async _storeResponseData(action, { text = '', id = 0 }) {
        switch (action.type) {
            case flowActionTypes.ACTION_REQUEST_INFO:
                await this._storeRequestResponse(action, text);
                break;
            case flowActionTypes.ACTION_QUESTION:
                await this._storeQuestionAnswer(action, { text, id });
                break;
            case flowActionTypes.ACTION_TICKET:
                await this._saveTicketContactInfo(action, text);
                break;
            case flowActionTypes.ACTION_PATRON_RECORDS:
                await this.#storePatronRecordResponse(action, text);
                break;
        }
        // Save searches and responses to searches
        if (action.isSearchAction()) {
            let response_id = 0;
            if (id === searchResponseTypes.RESOLVED) {
                response_id = actionResponseTypes.ACTION_RESPONSE_RESOLVED;
            } else if (id === searchResponseTypes.UNRESOLVED) {
                response_id = actionResponseTypes.ACTION_RESPONSE_UNRESOLVED;
            }
            this.botService.saveSessionAction(
                this.patronSession.getChannel(),
                action,
                {
                    response_id,
                    response: text,
                },
            );
        }
        if (action.isResourceAction() && id > 0) {
            // only save last response in bot booking
            if (action.botAppt && action.stage !== bookingStages.FORM) {
                return;
            }
            // convert to actionResponseTypes
            const response_id = id === linkResponseTypes.NO
                ? actionResponseTypes.ACTION_RESPONSE_HELP_NO
                : actionResponseTypes.ACTION_RESPONSE_HELP_YES;

            this.botService.saveSessionAction(
                this.patronSession.getChannel(),
                action,
                {
                    response_id,
                },
            );
        }
    }

    /**
     * Handle the response to start/end/skip chat
     * @param {FlowAction} action Welcome message action
     * @param {Number} responseId Response id
     */
    _handleWelcomeResponse(action, responseId) {
        // I don't think we need to save these responses.
        // answering "Yes" starts the bot session so it effectively does that.
        const respResult = action.getResultForResponse({ id: responseId });
        if (respResult.nextActionId > 0) {
            // start chat, which needs a server request...
            this.emitter.trigger('bot:startsession', {
                nextActionId: respResult.nextActionId,
                flowUuid: this.flow.uuid,
            });
            return;
        }

        // skip to chat
        if (respResult.nextActionId === flowActionIds.ACTION_SKIP) {
            // skip to normal chat.
            this.isSkipped = true;
            this.emitter.trigger('bot:skipbot');
            return;
        }

        // Save bot dismissal action
        this.botService.saveDismissal(this.widget.get('id'))
            .catch(() => {
                // No need to display this in UI
            });

        // else end... flowActionIds.ACTION_END
        this._handleEndAction();
    }

    /**
     * Handle multiple choice responses.
     * @param {Number} actionId Action's id.
     * @param {Number} responseId Response id that was clicked.
     */
    _handleResponse({ actionId, responseId }) {
        this._resetBotTimeout();
        const action = this.flow.getAction(actionId);
        if (!action) {
            this._sendErrorToWidget({
                actionId,
                endChat: true,
                message: this.flow.error_generic,
            });
            return;
        }
        if (action.type === flowActionTypes.ACTION_WELCOME) {
            this._handleWelcomeResponse(action, responseId);
            return;
        }

        this._storeResponseData(action, { id: responseId })
            .then(() => {
                this._handleNextAction(action, { id: responseId });
            })
            .catch((error) => {
                let endChat = false;
                if (error.code === botErrorCodes.botBlockedError) {
                    endChat = true;
                }
                this._sendErrorToWidget({
                    actionId: action.id,
                    message: error.message,
                    endChat,
                });
                if (endChat) {
                    return;
                }
                this._handleNextAction(action, { id: responseId });
            });
    }

    /**
     * Return more option/responses for an action to chat pane.
     * @param {Number} actionId Action to work from
     * @param {Number} page Page to show
     */
    #handleResponsePage({ actionId = 0, page = 0 }) {
        this._resetBotTimeout();
        const action = this.flow.getAction(actionId);
        if (!action) {
            this._sendErrorToWidget({
                actionId,
                endChat: true,
                message: this.flow.error_generic,
            });
            return;
        }
        const responses = action.getResponses({ page });
        this.emitter.trigger('bot:action:page', {
            actionId,
            actionType: action.type,
            responses,
        });
    }

    /**
     * Return previous stage of action
     * @param {Number} actionId Action to work from
     */
    #handleResponseBack({ actionId = 0 }) {
        this._resetBotTimeout();
        const action = this.flow.getAction(actionId);
        if (!action || !action.goToPreviousStage()) {
            this._sendErrorToWidget({
                actionId,
                endChat: true,
                message: this.flow.error_generic,
            });
            return;
        }
        // reprocess action, now with new stage
        this._processNextAction(action, {});
    }

    /**
     * Handle free text responses
     * @param {Number} actionId Current action id.
     * @param {String} text String from the input.
     */
    _handleFreeText({ actionId, text = '' }) {
        this._resetBotTimeout();
        const action = this.flow.getAction(actionId);
        if (!action) {
            this._sendErrorToWidget({
                actionId,
                endChat: true,
                message: this.flow.error_generic,
            });
            return;
        }

        if (!action.validateResponse(text)) {
            let errMsg = this.flow.error_input;
            if (action.type === flowActionTypes.ACTION_REQUEST_INFO && action.stage === requestStages.EMAIL) {
                errMsg = this.widget.get('translation').ch_validate_email || this.flow.error_input;
            }
            this._sendErrorToWidget({
                actionId: actionId,
                message: errMsg,
            });
            return;
        }

        if (action.type === flowActionTypes.ACTION_TICKET && action.stage === ticketStages.QUESTION) {
            this.botService.submitTicket(action, this.patronSession.getChannel(), text)
                .then(({ ticket_id }) => {
                    this.patronSession.ticketId = ticket_id;

                    const message = new BotMessage({
                        actionId: action.id,
                        actionType: action.type,
                        msg: action.success_message,
                    });
                    this._sendMessagesToWidget([message]);
                })
                .catch((error) => {
                    this._sendErrorToWidget({ actionId: action.id, message: error.message });
                })
                .finally(() => {
                    setTimeout(() => {
                        // if ticket was successful (id > 0)
                        // end chat, else next action
                        this._handleNextAction(action, { id: this.patronSession.ticketId });
                    }, this.messagePause);
                });
            return;
        }

        this._storeResponseData(action, { text })
            .then(() => {
                this._handleNextAction(action, { text });
            })
            .catch((error) => {
                let endChat = false;
                if (error.code === botErrorCodes.botBlockedError) {
                    endChat = true;
                }
                this._sendErrorToWidget({
                    actionId: action.id,
                    message: error.message,
                    endChat,
                });
                if (endChat) {
                    return;
                }
                this._handleNextAction(action, { text });
            });
    }

    /**
     * Save clicks to some action links (like FAQ, LG, LC).
     * @param {Number} actionId Action where link was shown
     * @param {String} url Clicked url
     */
    _handleClickSave({ actionId = 0, url = '' }) {
        if (actionId <= 0 || url === '') {
            return;
        }
        const action = this.flow.getAction(actionId);
        if (!action) {
            return;
        }

        // 1 is for ChatBotFlowConstants::ACTION_RESPONSE_CLICK
        this.botService.saveSessionAction(this.patronSession.getChannel(), action, { response_id: 1, response: url });
    }

    /**
     * From the patron records checkout list.
     * @param {String} itemId ID for checked out item
     */
    #handleRenewItem({ itemId = '', actionId = 0 }) {
        if (!itemId || actionId <= 0) {
            return;
        }
        this.botService.renewPatronItem(
            itemId,
            this.patronSession.libraryCard,
            this.patronSession.pin,
            this.patronSession.getChannel(),
        )
            .then((html) => {
                const action = this.flow.getAction(actionId);
                const message = new BotMessage({
                    actionId: action.id,
                    actionType: action.type,
                    msg: html,
                    responses: action.getResponses(),
                });
                this._sendMessagesToWidget([message]);
            })
            .catch((err) => {
                this._sendErrorToWidget({
                    actionId,
                    message: err.message,
                });
            });
    }

    /**
     * When send to chat comes back as offline.
     * @param {Number} actionId Current action
     * @param {Boolean} fallbackAttempt Was this the fallback check...
     */
    _handleChatOffline({ actionId = 0, fallbackAttempt = false }) {
        // Already tried fallback.
        if (fallbackAttempt) {
            this._handleChatFail({ actionId });
            return;
        }
        const action = this.flow.getAction(actionId);
        if (!action) {
            this._sendErrorToWidget({
                actionId,
                endChat: true,
            });
            return;
        }
        // Try with the default widget check
        if (action.useFallback) {
            this.emitter.trigger('bot:sendtochat', { actionId: action.id, fallbackAttempt: true });
            return;
        }
        this._handleChatFail({ actionId });
    }

    /**
     * When send to chat fails.
     * @param {Number} actionId Sent to chat action id
     */
    _handleChatFail({ actionId = 0 }) {
        const action = this.flow.getAction(actionId);
        if (!action || !action.fail_message) {
            this._sendErrorToWidget({
                actionId,
                endChat: true,
            });
            return;
        }

        // Create message.
        const message = new BotMessage({
            actionId: actionId,
            msg: action.fail_message,
        });
        this._sendMessagesToWidget([message]);
        // send right to next action.
        this._handleNextAction(action, {});
    }

    /**
     * Set the dynamically loaded actions when session starts.
     * @param {Number} actionId Next action to run
     * @param {Object} flow Chat flow data (including all actions)
     */
    _handleSessionLoad({ actionId, flow }) {
        this.flow.setActions(flow.actions || []);
        this.flow.setQuestions(flow.questions || []);

        // Trigger the first, post-welcome action.
        this._handleActionTrigger({ actionId });
    }

    /**
     * Start up bot, show welcome message.
     * @param {Boolean} chatOnline Is chat widget online
     */
    startBot(chatOnline = false) {
        if (this.isStarted) {
            return;
        }
        this.#isChatOnline = chatOnline;
        this.isStarted = true;
        this.emitter.trigger('bot:start');

        this.emitter.on('bot:response', this._handleResponse.bind(this));
        this.emitter.on('bot:response:page', this.#handleResponsePage.bind(this));
        this.emitter.on('bot:response:back', this.#handleResponseBack.bind(this));
        this.emitter.on('bot:freetext', this._handleFreeText.bind(this));
        this.emitter.on('bot:loadactions', this._handleSessionLoad.bind(this));
        this.emitter.on('bot:ticket:success', ({ action, ticket_id = 0 }) => {
            this.patronSession.ticketId = ticket_id;
            const message = new BotMessage({
                actionId: action.id,
                actionType: action.type,
                msg: action.success_message,
            });
            this._sendMessagesToWidget([message]);
            this._handleNextAction(action, { id: ticket_id });
        });
        this.emitter.on('bot:ticket:cancel', ({ action }) => {
            this._handleNextAction(action, {});
        });
        // LC Bot Booking form callback
        this.emitter.on('bot:booking:success', ({ action }) => {
            // save location/user
            this.botService.saveSessionAction(
                this.patronSession.getChannel(),
                action,
                {
                    response: action.getLocationAndUserName(),
                },
            );
            this._generateMessagesFromAction(action, {})
                .then((messages) => {
                    this._sendMessagesToWidget(messages);
                })
                .catch(() => {
                    this._sendErrorToWidget({
                        actionId: action.id,
                    });
                    // Move on to default next action.
                    this._handleNextAction(action, {});
                });
        });
        this.emitter.on('bot:booking:cancel', ({ action }) => {
            this._handleNextAction(action, {});
        });
        // used in preview only right now
        this.emitter.on('bot:triggeraction', this._handleActionTrigger.bind(this));
        this.emitter.on('bot:chatoffline', this._handleChatOffline.bind(this));
        this.emitter.on('bot:chatfail', this._handleChatFail.bind(this));
        this.emitter.on('bot:click', this._handleClickSave.bind(this));
        this.emitter.on('bot:message', this._sendMessagesToWidget.bind(this));
        this.emitter.on('bot:renewitem', this.#handleRenewItem.bind(this));

        // Trigger welcome message.
        const nextAction = this.flow.getWelcomeAction();
        if (this.#isChatOnline) {
            nextAction.setChatOnline();
        }
        this._generateMessagesFromAction(nextAction, {})
            .then((messages) => {
                this._sendMessagesToWidget(messages);
            })
            .catch(() => {
                // Just stop chat now if welcome errored out.
                this._sendErrorToWidget({
                    actionId: nextAction.id,
                    endChat: true,
                });
            });
    }

    stopBot() {
        this.isStarted = false;
        this._clearBotTimeout();
        this.emitter.trigger('bot:stop');
    }
}

export {
    BotController,
};
